<template>
  <b-overlay :show="loading">
    <b-alert
      variant="danger"
      :show="patientData === undefined && !loading"
    >
      <h4 class="alert-heading">
        Error fetching Patient details
      </h4>
      <div class="alert-body">
        <b-link
          class="alert-link"
          :to="{ name: 'admin-patients'}"
        >
          Patients
        </b-link>
      </div>
    </b-alert>

    <template v-if="patientData">
      <div>
        <b-row class="match-height">
          <b-col cols="12" md="9">
            <b-row class="match-height">
              <b-col cols="12" md="8">
                <patient-details-card :patient-data="patientData" :consultation-data="consultationData" />
              </b-col>

              <b-col cols="12" md="4">
                <patient-medications-card :consultation-data="consultationData" />
                <patient-vitals-card :patient-data="patientData" :consultation-data="consultationData" />
              </b-col>
            </b-row>

            <b-row class="match-height">
              <b-col cols="12" md="6">
                <doctors-note-card :consultation-data="consultationData" />
              </b-col>

              <b-col cols="12" md="6">
                <patient-lab-results-card :consultation-data="consultationData" />
              </b-col>
            </b-row>
          </b-col>

          <b-col cols="12" md="3">
            <patient-sidebar-nav :patient-data="patientData" :consultations-data="consultations" />
          </b-col>
        </b-row>
      </div>
    </template>
  </b-overlay>
</template>

<script>
import ListTable from "@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { BAlert, BAvatar, BBadge, BImg, BCard, BCol, BLink, BOverlay, BRow, BCardImg } from "bootstrap-vue"

import { camelCase, get, omit, startCase } from "lodash";
import VueEasyLightbox from 'vue-easy-lightbox';

import DoctorsNoteCard from './DoctorsNoteCard.vue';
import PatientSidebarNav from "./PatientSidebarNav.vue";
import PatientVitalsCard from './PatientVitalsCard.vue';
import UserViewActionCard from "./UserViewActionCard.vue";
import PatientDetailsCard from "./PatientDetailsCard.vue";
import ContributionsHistory from "./ContributionsHistory.vue";
import PatientLabResultsCard from "./PatientLabResultsCard.vue";
import PatientMedicationsCard from "./PatientMedicationsCard.vue";

export default {
  components: {
    ListTable,
    VueEasyLightbox,
    BCardImg,
    BOverlay,
    BAlert,
    BAvatar,
    BLink,
    BImg,
    BBadge,
    BRow,
    BCol,
    BCard,
    // components
    DoctorsNoteCard,
    PatientSidebarNav,
    PatientVitalsCard,
    PatientDetailsCard,
    UserViewActionCard,
    ContributionsHistory,
    PatientLabResultsCard,
    PatientMedicationsCard,
},
  directives: {},
  data(){
    return {
      loading: false,
      patientData: null,
      consultationData: null,

      consultations: [],

      backImageLoading: true,
      frontImageLoading: true,
      cardPreviewVisible: false,

      index: 0, // default: 0
    }
  },
  computed: {
    attachedImages() {
      const { path: src1, name: title1 } = this.getValueFromSource(this.patientData, 'national_ID_file', {});
      const { path: src2, name: title2 } = this.getValueFromSource(this.patientData, 'national_ID_file_back', {});

      const images = [];

      if (src1){
        images.push({
          src: src1,
          title: title1
        })
      }

      if (src2){
        images.push({
          src: src2,
          title: title2
        })
      }

      return images
    },
  },
  created() {
    this.fetchUserDetails();
  },
  methods: {
    async fetchUserDetails(){
      try {
        this.loading = true;
        const { id } = this.$route.params;

        const response = await this.useJwt().mainService.patients.fetch(id);
        this.patientData = get(response, 'data.data');

        await this.fetchMedicalSummary();
        await this.fetchMedicalHistory();
      } catch (error){
        this.patientData = undefined;

        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async fetchMedicalSummary(){
      try {
        this.loading = true;

        const { id } = this.$route.params;

        const request = await this.useJwt().mainService.patients.fetchMedicalSummary(id)
        const { data } = request.data;
        if (data){
          this.consultationData = data;
        }
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async fetchMedicalHistory(){
      try {
        this.loading = true;
        const { id } = this.$route.params;

        const query = {
          limit: 1000,
          patient_id: id,
        }

        const request = await this.useJwt().mainService.consultations.list(query);
        const { data } = request.data;
        this.consultations = data
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    getImageDataForPreview(image_data){
      const {
        path: src,
        name: title
      } = image_data
      return [{
        src,
        title
      }]
    },
    onImgLoad(path) {
      this[path] = false
    },
    showImg(index) {
      this.index = index
      this.cardPreviewVisible = true
    },
    handleCardPreviewVisibility() {
      this.cardPreviewVisible = false
    },
    onRowClicked(rowItem){
      this.$router.push({
        name: 'admin-loan-single',
        params: {
          id: rowItem._id
        } })
          .catch(() => {})
    },
    formatText(value) {
      return startCase(camelCase(value));
    },
    omitObject(object, path) {
      return omit(object, [path])
    }
  }
}
</script>

<style lang="scss">
small {
  font-size: 10px;
}
</style>
